import { RequireAuth } from '@components/RequireAuth';
import { RequireUpercut } from '@components/RequireRole';
import { DrawerProvider } from '@components/Sidebar/DrawerContext';
import { SidebarV2Provider } from '@components/SidebarV2';
import { SnackbarProvider } from '@components/Snackbar/SnackbarProvider';
import { ColorModeContext } from '@components/ThemeSwitcher/ThemeSwitcher';
import PermissionErrorBoundary from '@components/upercut-permissions/PermissionErrorBoundary';
import { AuthProvider } from '@contexts/AuthContext';
import { PermissionProvider } from '@contexts/PermissionContext';
import { ProIdProvider } from '@contexts/ProIdContext';
import { isDevEnv } from '@helper/envs';
import { usePermissionSubscription } from '@hooks/usePermissionSubscription';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { darkTheme, lightTheme } from './lib/theme';
import { OptionsProvider } from './modules/talents/context/options-context';
import BlogPage from './pages/Blog/BlogPage';
import BrandPage from './pages/Brands/BrandPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { FirstLoginResetPasswordPage } from './pages/FirstLoginResetPasswordPage';
import { HomePage } from './pages/Homepage';
import JobManagementPage from './pages/JobOfferPage';
import JobOfferTemplatePage from './pages/JobOfferTemplatePage';
import QuestionnairePage from './pages/QuestionnairePage';
import JobApplicationsPage from './pages/JobApplicationsPage';
import { LoginPage } from './pages/LoginPage';
import PricingTableManagementPage from './pages/PricingTableManagementPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import TalentPage from './pages/Talents/TalentPage';
import UpercutPage from './pages/Upercuts/UpercutPage';
import ViewerContent from './pages/ViewerContent';
import RequireProId from '@components/RequireProId';
import { AuthLayout } from '@components/AuthLayout';
import CompanyPage from './pages/Companies/CompanyPage';
import TalentFavoritesPage from './pages/TalentFavoritesPage';
import TalentSearchPage from './pages/TalentSearchPage';
import ScoringPage from './pages/ScoringPage';
import { TalentOperationsProvider } from '@modules/talents/context/TalentOperationsContext';
import { EntityNotesDialogProvider } from '@modules/notes/context/EntityNotesDialogContext';
import "./App.css";

const PermissionSubscriptionBridge = ({ children }) => {
    usePermissionSubscription();
    return <>{children}</>;
};

const App = () => {
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        const storedMode = localStorage.getItem('themeMode');
        return storedMode === 'light' ? 'light' : 'dark';
    });

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setMode(prevMode => {
                    const newMode = prevMode === 'light' ? 'dark' : 'light';
                    localStorage.setItem('themeMode', newMode);
                    return newMode;
                });
            },
        }),
        [],
    );

    const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode]);

    return (
        <HelmetProvider>
            <ColorModeContext.Provider value={colorMode}>
                <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <OptionsProvider>
                            <CssBaseline />
                            <AuthProvider>
                                <ProIdProvider>
                                    <DrawerProvider>
                                        <SidebarV2Provider>
                                        <SnackbarProvider>
                                            <PermissionProvider>
                                                <PermissionErrorBoundary>
                                                    <PermissionSubscriptionBridge>
                                                        <BrowserRouter>
                                                            <Routes>
                                                                {/* Public routes */}
                                                                <Route path="/auth/login" element={<LoginPage />} />
                                                                <Route path="/login" element={<LoginPage />} />
                                                                <Route path="/auth/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
                                                                <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
                                                                <Route path="/auth/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
                                                                <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
                                                                <Route path="/auth/first-login-reset-password" element={<AuthLayout><FirstLoginResetPasswordPage /></AuthLayout>} />
                                                                <Route path="/first-login-reset-password" element={<AuthLayout><FirstLoginResetPasswordPage /></AuthLayout>} />

                                                                {/* Pro dashboard route - requires Pro role */}
                                                                <Route
                                                                    path="/pro/*"
                                                                    element={
                                                                        <EntityNotesDialogProvider>
                                                                            <RequireAuth>
                                                                                <RequireProId showSelector>
                                                                                    <Routes>
                                                                                        {isDevEnv && <Route path="/dashboard" element={<JobManagementPage />} />}
                                                                                        {isDevEnv && <Route path="/applications/*" element={<JobApplicationsPage />} />}
                                                                                        {isDevEnv && <Route path="/brands" element={<BrandPage />} />}
                                                                                        {isDevEnv && <Route path="/job-offer-templates/*" element={<JobOfferTemplatePage />} />}
                                                                                        {/* Accessing / should redirect to /dashboard */}
                                                                                        <Route path="*" element={<Navigate to="/pro/dashboard" />} />
                                                                                    </Routes>
                                                                                </RequireProId>
                                                                            </RequireAuth>
                                                                        </EntityNotesDialogProvider>
                                                                    }
                                                                />

                                                                {/* Upercut-only routes - requires Upercut role */}
                                                                <Route
                                                                    path="/*"
                                                                    element={
                                                                        <EntityNotesDialogProvider>
                                                                            <TalentOperationsProvider>
                                                                                <RequireAuth>
                                                                                    <RequireUpercut>
                                                                                        <Routes>
                                                                                            <Route path="/" element={<HomePage />} />
                                                                                            <Route path="/blog/*" element={<BlogPage />} />
                                                                                            {isDevEnv && <Route path="/pricing-tables/*" element={<PricingTableManagementPage />} />}
                                                                                            {isDevEnv && <Route path="/job-offers/*" element={<JobManagementPage />} />}
                                                                                            {isDevEnv && <Route path="/applications/*" element={<JobApplicationsPage />} />}
                                                                                            {isDevEnv && <Route path="/talents/favorites/*" element={<TalentFavoritesPage />} />}
                                                                                            {isDevEnv && <Route path="/talent-search/*" element={<TalentSearchPage />} />}
                                                                                            {isDevEnv && <Route path="/talents/*" element={<TalentPage />} />}
                                                                                            {isDevEnv && <Route path="/talent-favorites/*" element={<TalentFavoritesPage />} />}
                                                                                            {isDevEnv && <Route path="/companies/*" element={<CompanyPage />} />}
                                                                                            {isDevEnv && <Route path="/subscription-plans/*" element={<SubscriptionPlansPage />} />}
                                                                                            {isDevEnv && <Route path="/brands/*" element={<BrandPage />} />}
                                                                                            {isDevEnv && <Route path="/upercuts/*" element={<UpercutPage />} />}
                                                                                            {isDevEnv && <Route path="/settings/*" element={<SettingsPage />} />}
                                                                                            {isDevEnv && <Route path="/scoring/*" element={<ScoringPage />} />}
                                                                                            {isDevEnv && <Route path="/job-offer-templates/*" element={<JobOfferTemplatePage />} />}
                                                                                            {isDevEnv && <Route path="/questionnaires/*" element={<QuestionnairePage />} />}
                                                                                            <Route path="/viewer" element={<ViewerContent />} />
                                                                                            <Route path="*" element={<Navigate to="/" />} />
                                                                                        </Routes>
                                                                                    </RequireUpercut>
                                                                                </RequireAuth>
                                                                            </TalentOperationsProvider>
                                                                        </EntityNotesDialogProvider>
                                                                    }
                                                                />
                                                            </Routes>
                                                        </BrowserRouter>
                                                    </PermissionSubscriptionBridge>
                                                </PermissionErrorBoundary>
                                            </PermissionProvider>
                                        </SnackbarProvider>
                                        </SidebarV2Provider>
                                    </DrawerProvider>
                                </ProIdProvider>
                            </AuthProvider>
                        </OptionsProvider>
                    </LocalizationProvider>
                </ThemeProvider>
            </ColorModeContext.Provider>
        </HelmetProvider>
    );
};

export default App;
